/**
 * LIGHT module tunables. All colors are hex, point/spot intensities are
 * physical (candela, three.js r155+ lighting units), positions in meters.
 * Owned by LIGHT; see CONTRACTS.md.
 */
import type { Vec3 } from '../core/types'

// --- Ambient / moonless sky -------------------------------------------------
/**
 * Cool-blue hemisphere ambient: enough that rooflines and the street edge
 * read as silhouettes against haze instead of dissolving into void.
 */
export const HEMI_SKY_COLOR = 0x1a2030
export const HEMI_GROUND_COLOR = 0x0a0a0c
export const HEMI_INTENSITY_OUTDOOR = 0.4
/** Indoors the practicals carve the room — ambient drops to almost nothing. */
export const HEMI_INTENSITY_INDOOR = 0.06

/** Faint cold directional fill — a moonless overcast glow, not a real moon.
 * Kept low: it is shadowless, and anything higher flattens the sidewalks to
 * daylight grey (QA round 2 clue-interact failure). */
export const SKY_FILL_COLOR = 0x91a8cc
export const SKY_FILL_INTENSITY_OUTDOOR = 0.08
/** Zero indoors: the directional has no shadow camera and bled through walls. */
export const SKY_FILL_INTENSITY_INDOOR = 0
export const SKY_FILL_POSITION: Vec3 = { x: -18, y: 32, z: 8 }

// --- Street lamps (pools of light) ------------------------------------------
export const LAMP_COLOR = 0xffd9a0
/** Just below WORLD's lamp-head glass spheres (y ≈ 4.5). */
export const LAMP_HEIGHT = 4.45
/** WORLD lamp heads hang at x = ±5.5 (pole ±6.7, arm 1.2 toward street). */
export const LAMP_LATERAL_X = 5.5
/** Alternating west/east, matching WORLD posts (west −30/15, east −8/37). */
export const LAMP_Z_POSITIONS: readonly number[] = [-30, -8, 15, 37]
export const LAMP_INTENSITY_OUTDOOR = 900
export const LAMP_INTENSITY_INDOOR = 80
/** Long enough that adjacent pools overlap into a breadcrumb path. */
export const LAMP_DISTANCE = 26
export const LAMP_ANGLE = 0.58
/** Soft falloff — pools, not hard white blobs. */
export const LAMP_PENUMBRA = 0.68
export const LAMP_DECAY = 1.5
export const LAMP_SHADOW_MAP_SIZE = 512
export const LAMP_SHADOW_BIAS = -0.0008
export const LAMP_SHADOW_NEAR = 0.5

// --- Neon sign light ----------------------------------------------------------
/**
 * Just off the face of WORLD's "THE BLUE DAHLIA" sign — face plane at
 * x ≈ 9.76, y 4.4, z −12, facing −X (east facade). Color matches the sign's
 * emissive.
 */
export const NEON_POSITION: Vec3 = { x: 8.6, y: 4.4, z: -12 }
/** Matches WORLD's saturated tube blue (0x1fb6ff). */
export const NEON_COLOR = 0x1fb6ff
/** Strong enough that the brick wall is visibly washed in the neon's blue —
 * the colored bounce is the whole shot (but below full flood: the letterforms
 * keep luminance priority). */
export const NEON_INTENSITY = 180
export const NEON_DISTANCE = 18
export const NEON_DECAY = 2
export const NEON_INDOOR_SCALE = 0.3
/** Flicker shape: fast buzz + slow swell + random dropouts. */
export const NEON_BUZZ_HZ_A = 57.0
export const NEON_BUZZ_HZ_B = 23.7
export const NEON_BUZZ_DEPTH = 0.18
export const NEON_SWELL_HZ = 3.1
export const NEON_SWELL_DEPTH = 0.1
export const NEON_DROPOUT_SEGMENT_HZ = 6
export const NEON_DROPOUT_PROBABILITY = 0.12
export const NEON_DROPOUT_LEVEL = 0.12
export const NEON_DEAD_LEVEL = 0.04

// --- Background practicals (depth layering down the street) -------------------
/**
 * Lit upper windows on WORLD's facades (facade fronts at x = ±10; see
 * WORLD's lit-window meshes at the same coordinates). Low-intensity warm
 * points so the bricks around each window pick up a gradient.
 */
export interface PracticalDef {
  readonly position: Vec3
  readonly color: number
  readonly intensity: number
  readonly distance: number
}
export const PRACTICAL_DEFS: readonly PracticalDef[] = [
  { position: { x: 9.3, y: 6.8, z: -28 }, color: 0xffc473, intensity: 5, distance: 9 },
  { position: { x: 9.3, y: 5.2, z: 8 }, color: 0xffd9a0, intensity: 4, distance: 8 },
  { position: { x: -9.3, y: 7.4, z: -16 }, color: 0xc9d6ee, intensity: 4, distance: 8 },
  // Fourth lonely window on the east mass near the south end — landmark for
  // the street-wide frame's right-side building (curtained amber).
  { position: { x: 9.3, y: 6.2, z: 24 }, color: 0xffb763, intensity: 4, distance: 8 }
]
export const PRACTICAL_DECAY = 2
export const PRACTICAL_INDOOR_SCALE = 0.4

// --- Clue accent (the one warm pool over the matchbook) -----------------------
/** Above WORLD's matchbook clue at (7.4, 0.17, −10.6). */
export const CLUE_SPOT_POSITION: Vec3 = { x: 7.4, y: 3.4, z: -10.6 }
export const CLUE_SPOT_COLOR = 0xffe2b0
export const CLUE_SPOT_INTENSITY_OUTDOOR = 46
export const CLUE_SPOT_INTENSITY_INDOOR = 4
export const CLUE_SPOT_DISTANCE = 7
/** Tight motivated pool — the player walks from darkness INTO the lit clue. */
export const CLUE_SPOT_ANGLE = 0.34
export const CLUE_SPOT_PENUMBRA = 0.5
export const CLUE_SPOT_DECAY = 1.6

// --- Volumetric lamp cones + wet-street glow ----------------------------------
/** Peak additive alpha of a lamp's visible light cone. */
export const CONE_OPACITY = 0.17
/** Cone radius at street level ≈ tan(LAMP_ANGLE) * LAMP_HEIGHT, pulled in. */
export const CONE_TOP_RADIUS = 0.16
export const CONE_BOTTOM_RADIUS = 2.4
export const CONE_RADIAL_SEGMENTS = 24
/** Fresnel power — higher = brighter core, softer silhouette edge. */
export const CONE_FRESNEL_POWER = 2.6
/** Additive haze quad hanging in front of the neon sign. */
export const NEON_HAZE_OPACITY = 0.07
export const NEON_HAZE_SIZE = 7.5

/** Fake wet-asphalt reflection smears (additive ground quads). Narrow and
 * long so each lamp doubles as a vertical streak down the rain-slick road —
 * the single biggest noir signal (QA round 2). ~0.4 of source read. */
export const GLOW_LAMP_OPACITY = 0.45
export const GLOW_LAMP_WIDTH = 1.7
export const GLOW_LAMP_LENGTH = 11
export const GLOW_NEON_OPACITY = 0.38
export const GLOW_NEON_WIDTH = 3
export const GLOW_NEON_LENGTH = 7
/** Hover height so the quads never z-fight the road. */
export const GLOW_HEIGHT = 0.03
export const GLOW_TEXTURE_SIZE_PX = 128

// --- Office rig ----------------------------------------------------------------
/**
 * Hard key spotlight just inside WORLD's east-wall window (glass at x = 40,
 * opening y 1.0–2.5, z ±1.2; slats at x = 39.84), raking back across the
 * office so the venetian gobo stripes the desk and west wall.
 */
export const KEY_POSITION: Vec3 = { x: 39.6, y: 2.3, z: 0 }
export const KEY_TARGET: Vec3 = { x: 32, y: 0.5, z: -0.6 }
export const KEY_COLOR = 0xbfd3f2
export const KEY_INTENSITY_INDOOR = 430
export const KEY_INTENSITY_OUTDOOR = 36
export const KEY_DISTANCE = 26
export const KEY_ANGLE = 0.5
export const KEY_PENUMBRA = 0.15
export const KEY_DECAY = 1.4
export const KEY_SHADOW_MAP_SIZE = 1024
export const KEY_SHADOW_BIAS = -0.0006
export const KEY_SHADOW_NEAR = 0.3

/**
 * Over WORLD's desk at (37.6, top 0.78, −0.6) — a warm downward spot under
 * WORLD's lamp-shade cone so it throws a defined elliptical pool on the desk
 * instead of flaring the whole room.
 */
export const DESK_LAMP_POSITION: Vec3 = { x: 37.3, y: 1.18, z: -0.6 }
export const DESK_LAMP_TARGET: Vec3 = { x: 37.3, y: 0.78, z: -0.6 }
export const DESK_LAMP_COLOR = 0xffd9a0
/** Pulled ~40% so the lampshade and desk objects stay legible (no clipping). */
export const DESK_LAMP_INTENSITY_INDOOR = 5.6
export const DESK_LAMP_INTENSITY_OUTDOOR = 1.4
export const DESK_LAMP_DISTANCE = 4
export const DESK_LAMP_ANGLE = 1.05
export const DESK_LAMP_PENUMBRA = 0.6
export const DESK_LAMP_DECAY = 2

// --- Window light shaft (visible blades of light through the blinds) ----------
export const SHAFT_OPACITY = 0.028
export const SHAFT_LENGTH = 5.2
export const SHAFT_WIDTH = 2.3
/** Midpoint + downward tilt matching the key light's rake. */
export const SHAFT_CENTER: Vec3 = { x: 37.2, y: 1.35, z: -0.2 }
export const SHAFT_TILT_RAD = 0.23
export const SHAFT_STRIPE_COUNT = 7

// --- Venetian gobo texture ------------------------------------------------------
export const GOBO_SIZE_PX = 256
export const GOBO_SLAT_COUNT = 9
/** Fraction of each slat period that is open (lit). */
export const GOBO_OPEN_FRACTION = 0.55
export const GOBO_EDGE_SOFTNESS_PX = 3

// --- Zone crossfade / finale -------------------------------------------------------
/** Exponential settle rate (1/s); ~95% settled within 1 second. */
export const ZONE_FADE_RATE = 3.2
export const LOST_GLOBAL_DIM = 0.45

// --- Rain -------------------------------------------------------------------------
export const RAIN_DROP_COUNT = 900
export const RAIN_FIELD_X_MIN = -10
export const RAIN_FIELD_X_MAX = 10
export const RAIN_FIELD_Z_MIN = -40
export const RAIN_FIELD_Z_MAX = 40
export const RAIN_FIELD_HEIGHT = 14
export const RAIN_FALL_SPEED = 9
export const RAIN_STREAK_LENGTH = 0.45
/** Slight horizontal slant of the streaks (wind). */
export const RAIN_WIND_X = 0.08
export const RAIN_COLOR = 0xaec6e8
/** Streaks in shadow are nearly invisible — rain reads only where light is. */
export const RAIN_BASE_BRIGHTNESS = 0.018
/** Per-light glow gain and inverse-square falloff scale for streak lighting.
 * ~2x scatter so the rain is bright crossing the light volumes and invisible
 * in shadow. */
export const RAIN_LIGHT_GAIN = 3.2
export const RAIN_LIGHT_FALLOFF = 0.28
export const RAIN_TAIL_FADE = 0.65
export const RAIN_ALPHA = 0.5
export const RAIN_INDOOR_SCALE = 0.55
/** Number of light positions fed to the rain shader (fixed GLSL array size). */
export const RAIN_LIGHT_COUNT = 10

// --- Office low fill -------------------------------------------------------------
/**
 * Faint warm fill above the desk so desk surface, wall planes, and floor
 * read as distinct surfaces instead of unparseable black (QA round 2).
 */
export const OFFICE_FILL_POSITION: Vec3 = { x: 36.8, y: 2.4, z: -0.4 }
export const OFFICE_FILL_COLOR = 0xffe8c0
export const OFFICE_FILL_INTENSITY_INDOOR = 1.8
export const OFFICE_FILL_INTENSITY_OUTDOOR = 0.4
export const OFFICE_FILL_DISTANCE = 9
export const OFFICE_FILL_DECAY = 2
